import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ArrowRight, Check } from "lucide-react";
import { DocumentVersionStatus } from "~/config/types";
import { useMyQuizzes } from "~/api/v2/useMyQuizzes";
import { useDocumentVersions } from "~/api/v2/useDocumentVersions";
import { useTags } from "~/api/swr/useTags";
import { type MyQuiz } from "~/api/types";
import { type AnchoringUser } from "~/api/v2/useAnchoringDocuments";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "~/atoms/spinner";
import ErrorState from "~/molecules/ErrorState";
import { AnchoringTable, type AnchoringColumn } from "./AnchoringTable";
import {
  CreatedByCell,
  DateCell,
  TagsCell,
  TitleLinkCell,
  UserCell,
} from "./cells";

interface MyQuizRow extends MyQuiz {
  owner: AnchoringUser | null;
  publisher: AnchoringUser | null;
  adoptedAt: string | null;
  tags: string[];
}

export const MyQuizzesTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const myQuizzes = useMyQuizzes();
  const versions = useDocumentVersions({
    statuses: [DocumentVersionStatus.published],
  });
  const tags = useTags();

  const rows = useMemo<MyQuizRow[]>(() => {
    const tagTitleById = new Map(
      (tags.data ?? []).map((tag) => [tag.id, tag.title]),
    );
    const versionById = new Map(
      (versions.data ?? []).map((version) => [version.id, version]),
    );

    return (myQuizzes.data ?? [])
      .map((quiz) => {
        const version = versionById.get(quiz.documentVersionId);
        return {
          ...quiz,
          owner: version?.document.owner ?? null,
          publisher: version?.document.publisher ?? null,
          adoptedAt: version?.adoptedAt ?? null,
          tags: (version?.document.tagIds ?? [])
            .map((id) => tagTitleById.get(id))
            .filter((title): title is string => title !== undefined),
        };
      })
      .sort((a, b) => Number(a.completed) - Number(b.completed));
  }, [myQuizzes.data, versions.data, tags.data]);

  if (myQuizzes.isLoading) return <CenterSpinner />;
  if (myQuizzes.error) return <ErrorState />;

  const columns: AnchoringColumn<MyQuizRow>[] = [
    {
      key: "status",
      header: "",
      cell: (row) =>
        row.completed ? (
          <span className="text-success flex items-center gap-1.5">
            {t(($) => $.anchoring.status.completed)}
            <Check className="size-4" />
          </span>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => void navigate(`/anchoring/${row.id}`)}
          >
            {t(($) => $.anchoring.status.take)}
            <ArrowRight className="size-4" />
          </Button>
        ),
    },
    {
      key: "title",
      header: t(($) => $.anchoring.columns.title),
      cell: (row) => (
        <TitleLinkCell
          documentId={row.documentId}
          documentVersionId={row.documentVersionId}
          title={row.title}
          tab="quiz"
        />
      ),
    },
    {
      key: "createdBy",
      header: t(($) => $.anchoring.columns.createdBy),
      cell: (row) => <CreatedByCell createdBy={row.createdBy} />,
    },
    {
      key: "createdAt",
      header: t(($) => $.anchoring.columns.createdAt),
      cell: (row) => <DateCell value={row.createdAt} />,
    },
    {
      key: "owner",
      header: t(($) => $.anchoring.columns.owner),
      cell: (row) => <UserCell user={row.owner} />,
    },
    {
      key: "publisher",
      header: t(($) => $.anchoring.columns.publisher),
      cell: (row) => <UserCell user={row.publisher} />,
    },
    {
      key: "publishedAt",
      header: t(($) => $.anchoring.columns.publishedAt),
      cell: (row) => <DateCell value={row.publishedAt} />,
    },
    {
      key: "adoptedAt",
      header: t(($) => $.anchoring.columns.adoptedAt),
      cell: (row) => <DateCell value={row.adoptedAt} />,
    },
    {
      key: "tags",
      header: t(($) => $.anchoring.columns.tags),
      cell: (row) => <TagsCell tags={row.tags} />,
    },
  ];

  return (
    <AnchoringTable
      columns={columns}
      rows={rows}
      getRowId={(row) => row.id}
      emptyMessage={t(($) => $.anchoring.empty.mine)}
    />
  );
};
